import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar, CartesianGrid, Legend, Line, ComposedChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

import { useChartColors } from "./chartColors";
import { Panel, LoadingRows, ErrorState, EmptyState } from "./Panel";
import type { AnalyticsEnvelope, AttendanceRow, AnalyticsViewProps } from "./types";

type AttendanceData = AnalyticsEnvelope & {
  series: AttendanceRow[];
  totals: Record<string, number>;
};

export function AttendanceChartView({
  data,
  loading,
  error,
}: AnalyticsViewProps<AttendanceData>) {
  const { t } = useTranslation();
  const colors = useChartColors();
  const [asTable, setAsTable] = useState(false);

  const rows = data?.series ?? [];
  const toggle = (
    <button
      type="button"
      className="text-[12px] text-muted-foreground underline"
      onClick={() => setAsTable((v) => !v)}
    >
      {t("homework.analytics.viewAsTable")}
    </button>
  );

  return (
    <Panel
      title={t("homework.analytics.attendance.title")}
      action={rows.length ? toggle : undefined}
    >
      {loading ? (
        <LoadingRows />
      ) : error ? (
        <ErrorState message={t("homework.analytics.error")} />
      ) : !rows.length ? (
        <EmptyState message={t("homework.analytics.empty")} />
      ) : asTable ? (
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-muted-foreground">
              <th className="text-left">{t("homework.analytics.attendance.date")}</th>
              <th className="text-right">{t("homework.analytics.attendance.present")}</th>
              <th className="text-right">{t("homework.analytics.attendance.late")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.period}>
                <td>{r.period}</td>
                <td className="text-right tabular-nums">{r.present}</td>
                <td className="text-right tabular-nums">{r.late}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div role="img" aria-label={t("homework.analytics.attendance.title")}>
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
              <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="period" stroke={colors.axis} tick={{ fontSize: 12, fill: colors.axis }} />
              <YAxis stroke={colors.axis} tick={{ fontSize: 12, fill: colors.axis }} width={36} />
              <Tooltip />
              <Legend />
              <Bar dataKey="present" name={t("homework.analytics.attendance.present")}
                   fill={colors.series(0)} radius={[3, 3, 0, 0]} isAnimationActive={false} />
              <Line type="monotone" dataKey="late" name={t("homework.analytics.attendance.late")}
                    stroke={colors.series(3)} strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
      {data?.caveat && <p className="mt-2 text-[11px] text-muted-foreground">{data.caveat}</p>}
    </Panel>
  );
}
